import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { Component, PropsWithChildren } from 'react';
import AQtiveGuardFullLogo from '../../assets/images/aqtive-guard-full-logo.svg';
import { AuthenticationError, getErrorMessage } from '../errors/errors';
import { UrlHelper } from '../utils/UrlHelper';

type Props = PropsWithChildren<{}>;

type State = {
  error: { code: string | number; message: string } | null;
  loggingOut: boolean;
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { error: null, loggingOut: false };
  }

  static getDerivedStateFromError(e: unknown): State {
    const errorMessage = e instanceof Error ? e.message || e.name : 'UNKNOWN ERROR';
    console.warn(`An error occurred: ${errorMessage}`);

    if (e instanceof AuthenticationError) {
      // `ErrorBoundary` is outside the `BrowserRouter`, we cannot use `navigate`:
      // force navigation to logout with a page reload. Navigating to `LOGOUT_PAGE`
      // and receiving the redirect response might take a while, set the `loggingOut`
      // state in order to display an appropriate message and prevent rendering of
      // the controller of the current path.
      // This only ever happens if the API key is not found in the cache during
      // a React navigation scenario: if the Flask session is expired, the browser
      // will show directly the login page. Practically you can only hit this if you
      // clear your browser data while you are on the page: set a timeout to allow
      // users to see the message even if the connection is very fast.
      setTimeout(() => {
        window.location.assign(UrlHelper.generateLogoutUrl());
      }, 2500);
      return { error: null, loggingOut: true };
    }

    // Map the error to a friendly message
    return { error: { code: errorMessage, message: getErrorMessage(e) }, loggingOut: false };
  }

  render() {
    const { error, loggingOut } = this.state;
    if (!error && !loggingOut) {
      return this.props.children;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          height: 1,
          width: 1,
          position: 'fixed',
          top: 0,
          left: 0,
        }}
      >
        <Box sx={{ maxWidth: '32rem' }}>
          <Stack spacing={2}>
            <Box sx={{ mb: 4 }}>
              <AQtiveGuardFullLogo role="img" title={t('a11y.login.mainLogo')} />
            </Box>
            {error && (
              <>
                <Typography variant="h5" sx={{ color: 'text.primary' }}>
                  {t('loadErrorMessage')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {error.message}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {error.code}
                </Typography>
              </>
            )}
            {loggingOut && (
              <>
                <Typography variant="h5" sx={{ color: 'text.primary' }}>
                  {t('loggingOut')}
                </Typography>
                <Box sx={{ p: 4 }}>
                  <CircularProgress color="inherit" />
                </Box>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    );
  }
}
